import { useState, useEffect } from "react";
import { useStripeProducts } from "#next/hooks/use-stripe-products";
import { useSubscription } from "#next/hooks/use-subscription";
import { formatAmountForDisplay } from "#/lib/stripe/utils";

export interface PricingTier {
  id: string | null,
  tier: string,
  name: string,
  description: string,
  monthlyPrice: string | null,
  usagePrice: string | null,
  usageDetails: string[] | null,
  color: string,
  cta: string,
  popular: boolean,
  priceIds?: string[],
  willCancelOn?: string | null
}

// Base pricing tiers data structure
export const basePricingTiers: PricingTier[] = [
  {
    id: null,
    tier: "free",
    name: "Free",
    description: "Basic access for casual users",
    monthlyPrice: "$0",
    usagePrice: null,
    usageDetails: [ "75 commands per month" ],
    color: "from-indigo-600 to-blue-700 text-white",
    cta: "Current Plan",
    popular: false
  },
  {
    id: process.env.NEXT_PUBLIC_STRIPE_PRO_ID,
    tier: "pro",
    name: "Pro",
    description: "Enhanced access for power users",
    monthlyPrice: null, // Will be populated from Stripe
    usagePrice: null,
    usageDetails: null, // Will be populated from Stripe
    color: "from-purple-600 to-violet-800 text-white",
    cta: "Upgrade",
    popular: true,
    priceIds: []
  },
  {
    id: process.env.NEXT_PUBLIC_STRIPE_ENTERPRISE_ID,
    tier: "enterprise",
    name: "Enterprise",
    description: "Advanced capabilities for teams and businesses",
    monthlyPrice: null, // Will be populated from Stripe
    usagePrice: null,
    usageDetails: null, // Will be populated from Stripe
    color: "from-emerald-600 to-teal-800 text-white",
    cta: "Upgrade",
    popular: false,
    priceIds: []
  }
];

export function usePricingTiers() {
  const [ pricingTiers, setPricingTiers ] = useState<PricingTier[]>(basePricingTiers);
  const { data: subscription } = useSubscription();
  useEffect(() => {
    console.log("subscription", subscription);
  }, [ subscription ]);

  const { data: stripeProducts, isLoading: productsLoading } = useStripeProducts();

  // Determine current subscription tier
  const currentTierProductId = subscription?.productId || null;

  // Update pricing details from Stripe when products are loaded
  useEffect(() => {
    if (!stripeProducts || !stripeProducts.length) {
      return;
    }

    setPricingTiers(prev =>
      prev.map(tier => {
        // Update Pro tier with actual pricing from Stripe
        const pro = stripeProducts.find(product => product.tier === "pro");
        const enterprise = stripeProducts.find(product => product.tier === "enterprise");
        if (!pro?.prices?.[0]) {
          return tier;
        } else if (!enterprise?.prices?.[0] || !enterprise?.prices?.[1]) {
          return tier;
        }

        // Pass willCancelOn data if the current subscription matches this tier
        const willCancelOn = subscription?.productId === tier.id ? subscription.willCancelOn : null;

        if (tier.tier === "pro" && pro) {
          const basePrice = pro.prices[0].unitAmount;
          const priceIds = pro.prices.map(price => price.id);

          // Set appropriate CTA based on current subscription
          let cta = "Upgrade";
          if (currentTierProductId === pro.id) {
            cta = "Current Plan";
          } else if (enterprise && currentTierProductId === enterprise.id) {
            cta = "Downgrade";
          }

          return {
            ...tier,
            name: pro.name || tier.name,
            monthlyPrice: formatAmountForDisplay(basePrice),
            usageDetails: [
              pro?.description || "75 commands per month"
            ],
            cta,
            priceIds,
            willCancelOn
          };
          // Update Enterprise tier with actual pricing and usage details from Stripe
        } else if (tier.tier === "enterprise" && enterprise) {
          const price1 = enterprise.prices[0].unitAmount;
          const price2 = enterprise.prices[1].unitAmount;
          if (!price1 || !price2) {
            return tier;
          }
          let basePrice: number;
          let usagePrice: number;
          if (price1 < price2) {
            usagePrice = price1;
            basePrice = price2;
          } else {
            usagePrice = price2;
            basePrice = price1;
          }
          const priceIds = enterprise.prices.map(price => price.id);

          // Calculate included commands based on base price and usage rate
          const includedCommands = Math.floor(basePrice / usagePrice);

          const formattedIncludedCommands = new Intl.NumberFormat("en-US").format(includedCommands);

          // Construct usage details description
          const usageDetails: string[] = [
            `Includes ${formattedIncludedCommands} commands per month.`,
            `Usage pricing of $${usagePrice} per 100 command applies after included commands.`
          ];

          // Set appropriate CTA based on current subscription
          let cta = "Upgrade";
          if (currentTierProductId === enterprise.id) {
            cta = "Current Plan";
          }

          return {
            ...tier,
            name: enterprise.name || tier.name,
            monthlyPrice: formatAmountForDisplay(basePrice),
            usagePrice: formatAmountForDisplay(usagePrice),
            usageDetails,
            cta,
            priceIds,
            willCancelOn
          };
        } else if (tier.tier === "free") {
          // Set appropriate CTA for free tier based on current subscription
          let cta = "Current Plan";
          if (pro && enterprise && (currentTierProductId === pro.id || currentTierProductId === enterprise.id)) {
            cta = "Downgrade to Free";
          }

          return {
            ...tier,
            cta,
            willCancelOn: null // Free tier doesn't have cancellation
          };
        }
        return tier;
      })
    );
  }, [ stripeProducts, currentTierProductId, subscription ]);

  return {
    pricingTiers,
    currentTierProductId,
    isLoading: productsLoading
  };
}